import {
    Card,
    CardComplete,
    CardContent,
    CardDetail,
    CardFront,
    CardHeader,
    CardSubtitle,
    CardTitle,
} from '@plato/ui/containers/card';
import type { SerializedEditorState, SerializedLexicalNode } from 'lexical';
import { CheckIcon, UserIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../../../components/accordion/accordion.tsx';
import { Button } from '../../../components/ui/button.tsx';
import Text from '../../../components/ui/text.tsx';
import { formatMonthShortDay } from '../../../lib/date-helpers.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CrmTaskDTO } from '../../../server/dto/crm-task-dto.ts';
import type { PlatoUserDTO } from '../../../server/dto/plato-user-dto.ts';
import type { CrmTaskUpdateWithId } from '../../../server/queries/crm-log-interaction-create.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';

const INITIAL_VISIBLE = 5;

const extractNodeText = (node: SerializedLexicalNode): string => {
    if ('text' in node && typeof node.text === 'string') {
        return node.text;
    }
    if ('children' in node && Array.isArray(node.children)) {
        return node.children.map(extractNodeText).join('');
    }
    return '';
};

const deriveTaskText = (descriptionRich: SerializedEditorState | null): { title: string; detail: string } => {
    const paragraphs =
        descriptionRich?.root.children
            .map(extractNodeText)
            .map((line) => line.trim())
            .filter(Boolean) ?? [];

    const [titleLine, ...detailLines] = paragraphs;

    const title = titleLine ?? '';
    const detail = detailLines.join(' · ');

    return { title, detail };
};

type LogInteractionTaskListProps = {
    tasks: CrmTaskDTO[];
    taskUpdates: CrmTaskUpdateWithId[];
    onToggleCompleted: (task: CrmTaskDTO, completed: boolean) => void;
};

export const LogInteractionTaskList = ({
    tasks,
    taskUpdates,
    onToggleCompleted,
}: LogInteractionTaskListProps): ReactNode => {
    const { t, locale } = useLocale();
    const { userId } = useAppConfig();
    const [showAll, setShowAll] = useState(false);

    if (tasks.length === 0) {
        return null;
    }

    const visible = showAll ? tasks : tasks.slice(0, INITIAL_VISIBLE);
    const hiddenCount = tasks.length - visible.length;

    const taskAssignee = (user: PlatoUserDTO): string => {
        return user.id === userId ? t('notes__tasks_assignee_me', { ns: 'notes' }) : user.fullName;
    };

    return (
        <Accordion type="single" collapsible defaultValue="tasks">
            <AccordionItem value="tasks">
                <AccordionTrigger className="py-3">
                    <Text size="base" weight="semibold" color="neutral-950">
                        {t('notes__tasks_section_title', { ns: 'notes' })}
                    </Text>
                </AccordionTrigger>
                <AccordionContent>
                    <div className="flex flex-col gap-2 pt-2">
                        {visible.map((task) => {
                            const completed = taskUpdates.some((u) => u.taskId === task.taskId);
                            const { title, detail } = deriveTaskText(task.descriptionRich);

                            const dueLabel = formatMonthShortDay(task.dueDate, locale);

                            return (
                                <label key={task.taskId} className="block cursor-pointer">
                                    <Card className={completed ? 'border-dashed' : undefined}>
                                        <CardFront className="card-completed:no-underline!">
                                            <CardComplete
                                                variant="primary"
                                                checked={completed}
                                                onChange={(event) => {
                                                    onToggleCompleted(task, event.target.checked);
                                                }}
                                            />
                                            <CardContent>
                                                <CardHeader className="flex-nowrap items-start">
                                                    <CardTitle
                                                        className={`line-clamp-2 min-w-0 flex-1${completed ? ' line-through' : ''}`}
                                                    >
                                                        {title}
                                                    </CardTitle>
                                                    <CardSubtitle className="shrink-0 whitespace-nowrap">
                                                        <UserIcon className="size-4" />{' '}
                                                        {taskAssignee(task.assignedTo)}
                                                        {dueLabel && (
                                                            <>
                                                                {' · '}
                                                                {completed && (
                                                                    <CheckIcon className="size-4" />
                                                                )}
                                                                {dueLabel}
                                                            </>
                                                        )}
                                                    </CardSubtitle>
                                                </CardHeader>
                                                {detail && !completed && (
                                                    <CardDetail className="line-clamp-3 text-content-weak">
                                                        {detail}
                                                    </CardDetail>
                                                )}
                                            </CardContent>
                                        </CardFront>
                                    </Card>
                                </label>
                            );
                        })}
                        {hiddenCount > 0 && (
                            <div>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                        setShowAll(true);
                                    }}
                                >
                                    {t('notes__tasks_show_more', { ns: 'notes', count: hiddenCount })}
                                </Button>
                            </div>
                        )}
                    </div>
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
};
